(() => {
  if (customElements.get('carryelle-video-proof')) return;
  class CarryelleVideoProof extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.track = this.querySelector('.proof-track');
      this.cards = [...this.querySelectorAll('[data-proof-card]')];
      this.states = new Map(this.cards.map(card => [card, { ratio: 0, stopped: false, manual: false }]));
      this.wanted = new Set();
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.previous = this.querySelector('[data-proof-prev]');
      this.next = this.querySelector('[data-proof-next]');
      this.onVisibility = () => this.queue();
      this.onMotion = () => this.queue();
      this.onScroll = () => this.updateNavigation();
      this.onPrevious = () => this.move(-1);
      this.onNext = () => this.move(1);
      this.previous.addEventListener('click', this.onPrevious);
      this.next.addEventListener('click', this.onNext);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      document.addEventListener('visibilitychange', this.onVisibility);
      this.reducedMotion.addEventListener('change', this.onMotion);
      this.cards.forEach(card => {
        const video = card.querySelector('video');
        const button = card.querySelector('[data-proof-toggle]');
        video.muted = true;
        video.defaultMuted = true;
        const mark = playing => {
          card.dataset.playing = String(playing);
          button.setAttribute('aria-pressed', String(playing));
          button.setAttribute('aria-label', playing ? this.dataset.pauseLabel : this.dataset.playLabel);
        };
        video.addEventListener('playing', () => mark(true));
        video.addEventListener('pause', () => mark(false));
        video.addEventListener('emptied', () => mark(false));
        video.addEventListener('error', () => mark(false));
        button.addEventListener('click', () => {
          const state = this.states.get(card);
          state.stopped = !video.paused;
          state.manual = !state.stopped;
          if (!this.observer) state.ratio = 1;
          this.queue();
        });
      });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            const state = this.states.get(entry.target);
            state.ratio = entry.isIntersecting ? entry.intersectionRatio : 0;
            if (!entry.isIntersecting) state.manual = false;
          });
          this.queue();
        }, { threshold: [0, 0.1, 0.2, 0.55, 0.85, 1], rootMargin: '0px' });
        this.cards.forEach(card => this.observer.observe(card));
      }
      this.updateNavigation();
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.reducedMotion?.removeEventListener('change', this.onMotion);
      this.previous?.removeEventListener('click', this.onPrevious);
      this.next?.removeEventListener('click', this.onNext);
      this.track?.removeEventListener('scroll', this.onScroll);
      this.cards?.forEach(card => this.unload(card.querySelector('video')));
      this.initialized = false;
    }
    queue() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = 0; this.sync(); });
    }
    sync() {
      const visible = document.hidden ? [] : this.cards.filter(card => this.states.get(card).ratio > 0);
      const loaded = new Set(visible);
      const lastVisible = visible.length ? this.cards.indexOf(visible[visible.length - 1]) : -1;
      if (lastVisible >= 0 && lastVisible + 1 < this.cards.length) loaded.add(this.cards[lastVisible + 1]);
      this.wanted = new Set(visible.filter(card => {
        const state = this.states.get(card);
        return !state.stopped && state.ratio >= 0.2;
      }));
      this.cards.forEach(card => {
        const state = this.states.get(card);
        const video = card.querySelector('video');
        const poster = card.querySelector('[data-poster]');
        if (!loaded.has(card)) { this.unload(video); return; }
        if (!poster.hasAttribute('src')) poster.src = poster.dataset.poster;
        video.preload = 'auto';
        video.autoplay = this.wanted.has(card);
        if (!video.hasAttribute('src')) {
          video.src = video.dataset.source;
          video.load();
        }
        if (!this.wanted.has(card)) { video.pause(); return; }
        if (video.paused) video.play().then(() => {
          if (!this.wanted.has(card) || document.hidden) video.pause();
        }).catch(() => { card.dataset.playing = 'false'; });
      });
      this.dataset.loadedVideos = String(this.querySelectorAll('video[src]').length);
    }
    unload(video) {
      video.autoplay = false;
      video.preload = 'none';
      video.pause();
      if (video.hasAttribute('src')) { video.removeAttribute('src'); video.load(); }
    }
    move(direction) {
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 16;
      this.track.scrollBy({ left: direction * (this.cards[0].getBoundingClientRect().width + gap), behavior: this.reducedMotion.matches ? 'instant' : 'smooth' });
    }
    updateNavigation() {
      const end = this.track.scrollWidth - this.track.clientWidth;
      this.previous.disabled = this.track.scrollLeft <= 2;
      this.next.disabled = this.track.scrollLeft >= end - 2;
    }
  }
  customElements.define('carryelle-video-proof', CarryelleVideoProof);
})();

(() => {
  const reviews = document.querySelector('.carryelle-review-widget');
  if (!reviews) return;
  let pending = 0;
  const refresh = () => {
    pending = 0;
    const average = reviews.querySelector('.jm-average-rating-display > .jm-cluster');
    const score = average?.querySelector('.jm-text');
    if (!score) return;
    const value = Math.max(0, Math.min(5, parseFloat(score.textContent.replace(',', '.'))));
    if (!Number.isFinite(value)) return;
    let extra = average.querySelector('.carryelle-average-extra');
    if (!extra) {
      extra = document.createElement('div');
      extra.className = 'carryelle-average-extra';
      extra.innerHTML = '<span class="carryelle-rating-scale"></span><span class="carryelle-rating-stars" aria-hidden="true"><span>★★★★★</span><span class="carryelle-rating-stars-fill">★★★★★</span></span>';
      average.append(extra);
    }
    const scale = extra.querySelector('.carryelle-rating-scale');
    if (scale.textContent !== reviews.dataset.ratingScale) scale.textContent = reviews.dataset.ratingScale;
    extra.querySelector('.carryelle-rating-stars-fill').style.width = value * 20 + '%';
  };
  new MutationObserver(() => {
    if (!pending) pending = requestAnimationFrame(refresh);
  }).observe(reviews, { childList: true, subtree: true });
  refresh();
})();
